import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BANDS = [
  { min: 0,  max: 10,       label: 'Severe',        desc: 'Significant breathing pattern disturbance — worth a conversation with a clinician or breathing practitioner.' },
  { min: 10, max: 20,       label: 'Poor',          desc: 'Common with habitual overbreathing. Consistent practice usually shifts this within weeks.' },
  { min: 20, max: 30,       label: 'Below average', desc: 'A little tight. Regular short sessions build this steadily.' },
  { min: 30, max: 40,       label: 'Fair',          desc: 'A workable baseline — where most people land before training.' },
  { min: 40, max: 60,       label: 'Good',          desc: 'A relaxed, efficient breathing pattern.' },
  { min: 60, max: Infinity, label: 'Excellent',     desc: 'High tolerance to rising CO2 — common in trained athletes and long-term practitioners.' },
];
const SESSION_MS = 10 * 60 * 1000;
const REST_S = 60;
const RING_CIRC = 628;
const STORAGE_KEY = 'cp-history';

const fmtClock = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const bandFor = (cp) => BANDS.find((b) => cp >= b.min && cp < b.max) || BANDS[BANDS.length - 1];

const ControlPause = ({ onBack }) => {
  const [phase, setPhase] = useState('idle');   // idle | ready | holding | result | rest | summary
  const [sessionRemaining, setSessionRemaining] = useState(SESSION_MS);
  const [holdSecs, setHoldSecs] = useState(0);
  const [restRemaining, setRestRemaining] = useState(REST_S);
  const [attempts, setAttempts] = useState([]);
  const [lastCP, setLastCP] = useState(null);
  const [lastSession, setLastSession] = useState(null);
  const [history, setHistory] = useState([]);
  // Auto-pause state: fired by phone calls, tab switches, screen locks, etc.
  const [interrupted, setInterrupted] = useState(false);

  const sessionEndRef = useRef(null);
  const sessionIntRef = useRef(null);
  const holdStartRef = useRef(null);
  const holdRafRef = useRef(null);
  const restEndRef = useRef(null);
  const restIntRef = useRef(null);
  // Frozen state captured at the moment of interruption so we can resume cleanly.
  const pausedRef = useRef({ phase: null, holdElapsed: 0, sessionRemaining: 0, restRemaining: 0 });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch { /* ignore */ }
    return () => {
      if (sessionIntRef.current) clearInterval(sessionIntRef.current);
      if (restIntRef.current) clearInterval(restIntRef.current);
      if (holdRafRef.current) cancelAnimationFrame(holdRafRef.current);
    };
  }, []);

  const stopSessionClock = useCallback(() => {
    clearInterval(sessionIntRef.current);
    sessionIntRef.current = null;
    setSessionRemaining(SESSION_MS);
  }, []);

  const finishSession = useCallback(() => {
    if (holdRafRef.current) cancelAnimationFrame(holdRafRef.current);
    clearInterval(restIntRef.current);
    stopSessionClock();
    setAttempts((current) => {
      if (current.length === 0) {
        setPhase('idle');
        return [];
      }
      const best = Math.max(...current);
      const avg = current.reduce((a, b) => a + b, 0) / current.length;
      const session = {
        date: new Date().toISOString(),
        attempts: current.slice(),
        best: Math.round(best * 10) / 10,
        avg: Math.round(avg * 10) / 10,
      };
      setLastSession(session);
      setHistory((h) => {
        const next = [...h, session].slice(-50);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
      // Persist to backend (best-effort — session is preserved locally regardless).
      try {
        const token = localStorage.getItem('gntl_token');
        if (token && API_URL) {
          fetch(`${API_URL}/api/breathwork/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              attempts: session.attempts,
              best: session.best,
              avg: session.avg,
            }),
          }).catch(() => {});
        }
      } catch { /* ignore */ }
      setPhase('summary');
      return [];
    });
  }, [stopSessionClock]);

  const startSessionClock = useCallback(() => {
    sessionEndRef.current = Date.now() + SESSION_MS;
    setSessionRemaining(SESSION_MS);
    sessionIntRef.current = setInterval(() => {
      const rem = sessionEndRef.current - Date.now();
      setSessionRemaining(rem);
      if (rem <= 0) finishSession();
    }, 250);
  }, [finishSession]);

  // Auto-pause: called on visibility loss / window blur / pagehide (phone calls,
  // app switches, screen lock, incoming notifications, etc.). Freezes every live
  // timer and stores the snapshot so we can resume without losing progress.
  const pauseForInterruption = useCallback(() => {
    // Only pause during phases that have active timers.
    if (phase !== 'ready' && phase !== 'holding' && phase !== 'rest') return;
    if (interrupted) return;

    const snap = { phase, holdElapsed: 0, sessionRemaining: 0, restRemaining: 0 };

    // Freeze the hold clock (mid-breath-hold).
    if (holdRafRef.current) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    if (phase === 'holding' && holdStartRef.current != null) {
      snap.holdElapsed = (performance.now() - holdStartRef.current) / 1000;
    }

    // Freeze the total-session clock.
    if (sessionIntRef.current) {
      clearInterval(sessionIntRef.current);
      sessionIntRef.current = null;
    }
    if (sessionEndRef.current) {
      snap.sessionRemaining = Math.max(0, sessionEndRef.current - Date.now());
    }

    // Freeze the rest clock.
    if (restIntRef.current) {
      clearInterval(restIntRef.current);
      restIntRef.current = null;
    }
    if (phase === 'rest' && restEndRef.current) {
      snap.restRemaining = Math.max(0, Math.ceil((restEndRef.current - Date.now()) / 1000));
    }

    pausedRef.current = snap;
    setInterrupted(true);
  }, [phase, interrupted]);

  // Resume from the frozen snapshot: rebuilds each timer's target time from
  // the stored remaining values so no interruption time is counted.
  const resumeFromInterruption = useCallback(() => {
    const snap = pausedRef.current;
    if (!snap.phase) { setInterrupted(false); return; }

    // Session clock: restart from stored remaining.
    if (snap.sessionRemaining > 0) {
      sessionEndRef.current = Date.now() + snap.sessionRemaining;
      setSessionRemaining(snap.sessionRemaining);
      sessionIntRef.current = setInterval(() => {
        const rem = sessionEndRef.current - Date.now();
        setSessionRemaining(rem);
        if (rem <= 0) finishSession();
      }, 250);
    }

    // Hold clock: shift the start reference back so `now - start = prior elapsed`.
    if (snap.phase === 'holding') {
      holdStartRef.current = performance.now() - snap.holdElapsed * 1000;
      const tick = () => {
        const el = (performance.now() - holdStartRef.current) / 1000;
        setHoldSecs(el);
        holdRafRef.current = requestAnimationFrame(tick);
      };
      holdRafRef.current = requestAnimationFrame(tick);
    }

    // Rest clock: rebuild rest end from stored seconds.
    if (snap.phase === 'rest' && snap.restRemaining > 0) {
      restEndRef.current = Date.now() + snap.restRemaining * 1000;
      setRestRemaining(snap.restRemaining);
      restIntRef.current = setInterval(() => {
        const rem = restEndRef.current - Date.now();
        setRestRemaining(Math.max(0, Math.ceil(rem / 1000)));
        if (rem <= 0) { clearInterval(restIntRef.current); setPhase('ready'); }
      }, 250);
    }

    pausedRef.current = { phase: null, holdElapsed: 0, sessionRemaining: 0, restRemaining: 0 };
    setInterrupted(false);
  }, [finishSession]);

  // If interrupted mid-hold, offer to discard the hold and go back to Ready.
  const restartHoldFromInterruption = useCallback(() => {
    const snap = pausedRef.current;
    // Session clock resumes so we don't punish the user for the interruption.
    if (snap.sessionRemaining > 0) {
      sessionEndRef.current = Date.now() + snap.sessionRemaining;
      setSessionRemaining(snap.sessionRemaining);
      sessionIntRef.current = setInterval(() => {
        const rem = sessionEndRef.current - Date.now();
        setSessionRemaining(rem);
        if (rem <= 0) finishSession();
      }, 250);
    }
    setHoldSecs(0);
    setPhase('ready');
    pausedRef.current = { phase: null, holdElapsed: 0, sessionRemaining: 0, restRemaining: 0 };
    setInterrupted(false);
  }, [finishSession]);

  // Bind auto-pause to page-lifecycle events. `visibilitychange` fires on
  // incoming phone calls, app switching, screen lock, and tab switching on
  // every major mobile browser. `blur` + `pagehide` catch iOS edge cases.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') pauseForInterruption();
    };
    const onBlur = () => pauseForInterruption();
    const onPageHide = () => pauseForInterruption();

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [pauseForInterruption]);

  const beginHold = () => {
    holdStartRef.current = performance.now();
    setHoldSecs(0);
    setPhase('holding');
    const tick = () => {
      const el = (performance.now() - holdStartRef.current) / 1000;
      setHoldSecs(el);
      holdRafRef.current = requestAnimationFrame(tick);
    };
    holdRafRef.current = requestAnimationFrame(tick);
  };

  const endHold = () => {
    cancelAnimationFrame(holdRafRef.current);
    const el = (performance.now() - holdStartRef.current) / 1000;
    const cp = Math.round(el * 10) / 10;
    setLastCP(cp);
    setAttempts((a) => [...a, cp]);
    setPhase('result');
  };

  const beginRest = () => {
    restEndRef.current = Date.now() + REST_S * 1000;
    setRestRemaining(REST_S);
    setPhase('rest');
    restIntRef.current = setInterval(() => {
      const rem = restEndRef.current - Date.now();
      setRestRemaining(Math.max(0, Math.ceil(rem / 1000)));
      if (rem <= 0) { clearInterval(restIntRef.current); setPhase('ready'); }
    }, 250);
  };

  const sessionTimeLeft = sessionRemaining;
  const canAnother = sessionTimeLeft > 75000;
  const sessionClockLive = phase !== 'idle' && phase !== 'summary';
  const sessionClockWarn = sessionClockLive && sessionRemaining <= 2 * 60 * 1000;

  // Core display
  const coreClass = phase === 'holding' ? 'holding' : (phase === 'ready' || phase === 'rest' || phase === 'idle') ? 'breathing' : '';
  let eyebrow = 'Ready when you are';
  let number = '—';
  let unit = '';
  if (phase === 'ready') eyebrow = 'Breathe normally';
  if (phase === 'holding') { eyebrow = 'Holding'; number = holdSecs.toFixed(1); unit = 'seconds'; }
  if (phase === 'result') { eyebrow = 'This attempt'; number = lastCP.toFixed(1); unit = 'seconds'; }
  if (phase === 'rest') { number = restRemaining; unit = 's rest'; eyebrow = 'Resting'; }
  if (phase === 'summary' && lastSession) { eyebrow = 'Session best'; number = lastSession.best.toFixed(1); unit = 'seconds'; }

  return (
    <div style={{
      minHeight: '100vh', background: '#0E1310', color: '#EDEDE4',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      fontFamily: 'Inter, sans-serif', display: 'flex', justifyContent: 'center', position: 'relative',
    }} data-testid="control-pause">
      {interrupted && (
        <div
          data-testid="cp-interruption-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(14, 19, 16, 0.88)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 60,
          }}
        >
          <div style={{
            background: '#161C19', border: '1px solid #2A322C', borderRadius: 24,
            padding: '28px 24px', maxWidth: 380, width: '100%', textAlign: 'center',
          }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', margin: '0 auto 16px', border: '1px solid #5D7A67', background: '#1E2620', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#86AC93" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="4" width="6" height="16" rx="1" />
                <line x1="12" y1="17" x2="12" y2="17" />
              </svg>
            </div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontWeight: 500, fontSize: 22, margin: '0 0 8px' }}>
              Session paused
            </h2>
            <p style={{ color: '#8FA095', fontSize: 14, lineHeight: 1.55, margin: '0 0 22px' }}>
              {pausedRef.current.phase === 'holding'
                ? `You were mid-hold at ${pausedRef.current.holdElapsed.toFixed(1)}s when the app was interrupted. Take a normal breath — then choose how to continue.`
                : 'The app was interrupted. Everything is right where you left it.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={resumeFromInterruption}
                data-testid="cp-resume"
                style={{
                  background: '#86AC93', color: '#0E1310', border: 'none', borderRadius: 999,
                  padding: '12px 20px', fontSize: 14.5, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {pausedRef.current.phase === 'holding' ? 'Keep this hold' : 'Resume where I left off'}
              </button>
              {pausedRef.current.phase === 'holding' && (
                <button
                  onClick={restartHoldFromInterruption}
                  data-testid="cp-restart-hold"
                  style={{
                    background: 'transparent', color: '#8FA095', border: '1px solid #2A322C',
                    borderRadius: 999, padding: '11px 20px', fontSize: 14, cursor: 'pointer',
                  }}
                >
                  Discard hold — start fresh
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div style={{ width: '100%', maxWidth: 460, padding: '20px 20px 60px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#8FA095', cursor: 'pointer', display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, padding: 0, marginBottom: 10 }} data-testid="cp-back">
              <ArrowLeft size={14} /> Back
            </button>
            <h1 style={{ fontFamily: 'Fraunces, serif', fontWeight: 500, fontSize: 28, margin: 0, letterSpacing: '-0.01em' }}>Control Pause</h1>
            <p style={{ margin: '4px 0 0', color: '#8FA095', fontSize: 14 }}>Buteyko breath-hold trainer</p>
          </div>
          <div data-testid="cp-clock" style={{
            fontFamily: 'Fraunces, serif', fontVariantNumeric: 'tabular-nums', fontSize: 15,
            color: sessionClockWarn ? '#C08558' : sessionClockLive ? '#86AC93' : '#5E6D64',
            border: `1px solid ${sessionClockWarn ? '#8C5F3E' : sessionClockLive ? '#5D7A67' : '#2A322C'}`,
            borderRadius: 999, padding: '6px 12px', whiteSpace: 'nowrap',
          }}>{fmtClock(sessionClockLive ? sessionRemaining : SESSION_MS)}</div>
        </div>

        {/* Stage */}
        <div style={{ background: '#161C19', border: '1px solid #2A322C', borderRadius: 24, padding: '32px 24px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <div style={{ position: 'relative', width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 220 220" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              <circle cx="110" cy="110" r="100" fill="none" stroke="#1E2620" strokeWidth="6" />
              <circle cx="110" cy="110" r="100" fill="none" stroke="#86AC93" strokeWidth="6" strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={phase === 'holding' ? RING_CIRC * (1 - Math.min(holdSecs / 60, 1)) : phase === 'rest' ? RING_CIRC * (restRemaining / REST_S) : RING_CIRC}
                style={{ transition: 'stroke-dashoffset 0.2s linear' }}
              />
            </svg>
            <div className={coreClass} style={{
              position: 'absolute', width: 176, height: 176, borderRadius: '50%',
              background: 'radial-gradient(circle at 50% 40%, #1E2620, #161C19)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              animation: coreClass === 'breathing' ? 'cpBreathe 8s ease-in-out infinite' : coreClass === 'holding' ? 'cpPulse 3s ease-in-out infinite' : 'none',
            }}>
              <div style={{ fontSize: 12, color: '#8FA095' }}>{eyebrow}</div>
              <div style={{ fontFamily: 'Fraunces, serif', fontVariantNumeric: 'tabular-nums', fontSize: 44, fontWeight: 500, marginTop: 2 }} data-testid="cp-core-number">{number}</div>
              <div style={{ fontSize: 14, color: '#8FA095', marginTop: 2 }}>{unit}</div>
            </div>
          </div>

          <div style={{ textAlign: 'center', color: '#8FA095', fontSize: 14.5, lineHeight: 1.55, maxWidth: 300 }}>
            {phase === 'idle' && "Sit upright and breathe normally for a few breaths. When you're ready, begin your session — you'll then exhale gently and hold."}
            {phase === 'ready' && (<>Take a couple of easy breaths. <strong style={{ color: '#EDEDE4', fontWeight: 500 }}>Exhale gently, fully</strong> — then press Start the moment your lungs feel empty.</>)}
            {phase === 'holding' && (<>Stay relaxed. Stop at the <strong style={{ color: '#EDEDE4', fontWeight: 500 }}>first honest urge</strong> to breathe — not the point you're forced to gasp.</>)}
            {phase === 'result' && lastCP !== null && (<><span style={{ display: 'inline-block', marginBottom: 6, padding: '3px 10px', borderRadius: 999, fontSize: 12, background: 'rgba(134,172,147,0.22)', color: '#86AC93' }}>{bandFor(lastCP).label}</span><br />Breathe normally now — no big gasp. Rest a minute before another attempt.</>)}
            {phase === 'rest' && 'Breathe normally and let your body settle before the next hold.'}
            {phase === 'summary' && lastSession && (<><span style={{ display: 'inline-block', marginBottom: 6, padding: '3px 10px', borderRadius: 999, fontSize: 12, background: 'rgba(134,172,147,0.22)', color: '#86AC93' }}>{bandFor(lastSession.best).label}</span><br />{lastSession.attempts.length} hold{lastSession.attempts.length > 1 ? 's' : ''} logged · average {lastSession.avg.toFixed(1)}s</>)}
          </div>

          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            {phase === 'idle' && <Btn primary onClick={() => { startSessionClock(); setPhase('ready'); }} testid="cp-begin-session">Begin session</Btn>}
            {phase === 'ready' && <Btn primary onClick={beginHold} testid="cp-start-hold">Start hold</Btn>}
            {phase === 'holding' && <Btn stop onClick={endHold} testid="cp-stop-hold">Stop</Btn>}
            {phase === 'result' && (
              <>
                <Btn primary disabled={!canAnother} onClick={beginRest} testid="cp-rest">Rest 60s, try again</Btn>
                <Btn ghost onClick={finishSession} testid="cp-finish">Finish session</Btn>
              </>
            )}
            {phase === 'rest' && <Btn ghost onClick={() => { clearInterval(restIntRef.current); setPhase('ready'); }} testid="cp-skip-rest">Skip rest</Btn>}
            {phase === 'summary' && <Btn primary onClick={() => { setLastSession(null); setPhase('idle'); }} testid="cp-new-session">Start new session</Btn>}
          </div>
        </div>

        {/* Ladder */}
        <section>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontWeight: 500, fontSize: 19, margin: '0 0 14px' }}>Where you land</h2>
          <div style={{ background: '#161C19', border: '1px solid #2A322C', borderRadius: 20, padding: 20 }}>
            {BANDS.map((b, i) => {
              const cp = lastSession?.best ?? lastCP;
              const isCurrent = cp !== null && cp !== undefined && cp >= b.min && cp < b.max;
              return (
                <div key={b.label} style={{
                  display: 'flex', gap: 12, padding: '11px 10px',
                  borderTop: i === 0 ? 'none' : '1px solid #2A322C',
                  background: isCurrent ? 'rgba(134,172,147,0.22)' : 'transparent',
                  borderRadius: isCurrent ? 12 : 0, margin: isCurrent ? '0 -10px' : 0,
                }}>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 14, color: isCurrent ? '#86AC93' : '#8FA095', minWidth: 56 }}>
                    {b.max === Infinity ? `${b.min}s+` : `${b.min}–${b.max}s`}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: isCurrent ? '#86AC93' : '#EDEDE4' }}>{b.label}</div>
                    <div style={{ fontSize: 12.5, color: '#5E6D64', marginTop: 2 }}>{b.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <p style={{ fontSize: 12, color: '#5E6D64', lineHeight: 1.6, padding: '0 4px' }}>
          Control Pause is a self-check popularized by Buteyko practitioners, not a medical diagnosis.
          Stop any hold at the first honest urge to breathe. If you feel dizzy or unwell, stop and
          breathe normally. If your Control Pause stays under 10 seconds across several sessions, mention
          it to a doctor or breathing practitioner.
        </p>

        <style>{`
          @keyframes cpBreathe { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(134,172,147,0.22); } 50% { transform: scale(1.045); box-shadow: 0 0 0 14px transparent; } }
          @keyframes cpPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(134,172,147,0.22); } 50% { box-shadow: 0 0 22px 4px rgba(134,172,147,0.22); } }
        `}</style>
      </div>
    </div>
  );
};

const Btn = ({ children, primary, stop, ghost, disabled, onClick, testid }) => {
  const bg = primary ? '#86AC93' : stop ? '#C08558' : 'transparent';
  const color = primary || stop ? '#0E1310' : '#8FA095';
  const border = ghost ? '1px solid #2A322C' : 'none';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        border, background: bg, color, borderRadius: 14, padding: '15px 20px',
        fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer', width: '100%',
        opacity: disabled ? 0.4 : 1, transition: 'transform 0.15s ease, background 0.2s ease, opacity 0.2s ease',
      }}
    >
      {children}
    </button>
  );
};


export default ControlPause;
